import { X, Sparkles, Rocket, PartyPopper, Gift, Zap, Star, Heart, Bell, Megaphone, TrendingUp, Award, Target } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import ReactDOM from 'react-dom';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { MarkdownInline } from './MarkdownInline';

type FontSizeKey = 'sm' | 'md' | 'lg' | 'xl';
type FontStyleKey = 'regular' | 'bold' | 'italic';

interface TextFieldStyle {
  size?: FontSizeKey;
  style?: FontStyleKey;
}

interface WelcomeModalTextStyles {
  title?: TextFieldStyle;
  subtitle?: TextFieldStyle;
  message?: TextFieldStyle;
  cta?: TextFieldStyle;
}

interface WelcomeModalProps {
  isOpen: boolean;
  modal: {
    id: string;
    title: string;
    subtitle?: string;
    message: string;
    icon: string;
    ctaText: string;
    allowClose?: boolean;
    showButton?: boolean;
    textStyles?: WelcomeModalTextStyles | null;
  } | null;
  onClose: () => void;
  /** When set (e.g. 10001), portal is shown above admin overlay (z-[10000]) */
  portalZIndex?: number;
}

const SIZE_CLASSES: Record<FontSizeKey, string> = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
};
const TITLE_SIZE_CLASSES: Record<FontSizeKey, string> = {
  sm: 'text-xl',
  md: 'text-2xl',
  lg: 'text-3xl',
  xl: 'text-4xl',
};
const STYLE_CLASSES: Record<FontStyleKey, string> = {
  regular: 'font-normal',
  bold: 'font-bold',
  italic: 'italic',
};

function getTextClasses(
  field: TextFieldStyle | undefined,
  sizeMap: Record<FontSizeKey, string>,
  defaultSizeClass: string,
  defaultStyleClass: string
): string {
  const sizeClass = field?.size ? sizeMap[field.size] : defaultSizeClass;
  const styleClass = field?.style ? STYLE_CLASSES[field.style] : defaultStyleClass;
  return `${sizeClass} ${styleClass}`.trim();
}

// Map icon names to Lucide components
const iconMap: Record<string, any> = {
  Sparkles,
  Rocket,
  PartyPopper,
  Gift,
  Zap,
  Star,
  Heart,
  Bell,
  Megaphone,
  TrendingUp,
  Award,
  Target,
};

export function WelcomeModal({ isOpen, modal, onClose, portalZIndex }: WelcomeModalProps) {
  useModalFocus(isOpen);
  if (!modal) return null;

  const IconComponent = iconMap[modal.icon] || Sparkles;
  const allowClose = modal.allowClose !== undefined ? modal.allowClose : true;
  const ts = modal.textStyles;

  const titleClasses = getTextClasses(ts?.title, TITLE_SIZE_CLASSES, 'text-3xl', 'font-bold');
  const subtitleClasses = getTextClasses(ts?.subtitle, SIZE_CLASSES, 'text-lg', 'font-normal');
  const messageClasses = getTextClasses(ts?.message, SIZE_CLASSES, 'text-base', 'font-normal');
  const ctaClasses = getTextClasses(ts?.cta, SIZE_CLASSES, 'text-lg', 'font-medium');

  return ReactDOM.createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div key="welcome-modal" className="fixed inset-0" style={{ zIndex: portalZIndex ?? 'var(--z-important)' }}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md"
            onClick={allowClose ? onClose : undefined}
          />

          {/* Modal – overflow-y-auto + max-h so modal fits on small screens and scrolls when needed */}
          <div className="fixed inset-0 flex items-center justify-center p-4 pt-safe pb-24 pb-safe overflow-y-auto overscroll-contain ios-scroll">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[calc(100dvh-3rem)] overflow-y-auto my-auto ios-scroll"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-white p-8 relative text-center border-b border-gray-100">
                {allowClose && (
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    aria-label="Lukk"
                  >
                    <X className="w-5 h-5 text-gray-700" />
                  </button>
                )}

                {/* Icon */}
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl shadow-sm">
                    <IconComponent className="w-12 h-12 text-white" />
                  </div>
                </div>

                {/* Title – supports **bold** and *italic* Markdown */}
                <h2 className={`${titleClasses} text-gray-900 mb-2 text-center leading-tight break-words`}>
                  <MarkdownInline text={modal.title} as="span" />
                </h2>

                {/* Subtitle – supports **bold** and *italic* Markdown */}
                {modal.subtitle && (
                  <p className={`${subtitleClasses} text-gray-600 text-center leading-snug break-words`}>
                    <MarkdownInline text={modal.subtitle} as="span" />
                  </p>
                )}
              </div>

              {/* Content – message supports **bold**, *italic* and newlines */}
              <div className="p-8 space-y-6">
                <p className={`${messageClasses} text-gray-700 leading-relaxed text-center`}>
                  <MarkdownInline text={modal.message} preserveNewlines as="span" />
                </p>

                {/* CTA Button – supports **bold** and *italic* Markdown */}
                {modal.showButton && (
                  <button
                    onClick={onClose}
                    className={`w-full py-4 px-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all ${ctaClasses}`}
                  >
                    <MarkdownInline text={modal.ctaText} as="span" />
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
